import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ATMSwitch from 'src/components/atoms/FormElements/ATMSwitch/ATMSwitch';
import { FilterType } from 'src/components/molecules/MOLFilterBar/MOLFilterBar';
import { TableHeader } from 'src/components/molecules/MOLTable/MOLTable';
import { useFetchData } from 'src/hooks/useFetchData';
import { useFilterPagination } from 'src/hooks/useFilterPagination';
import { useGetCategoriesQuery } from 'src/modules/Category/service/CategoryServices';
import { useGetSubCategoriesQuery } from 'src/modules/SubCategory/service/SubCategoryServices';
import { RootState } from 'src/store';
import { CURRENCY } from 'src/utils/constants';
import ShowConfirmation from 'src/utils/ShowConfirmation';
import { showToast } from 'src/utils/showToaster';
import { Service } from '../../models/Service.model';
import {
  useDeleteServiceMutation,
  useGetServicesQuery,
  useServiceStatusMutation,
} from '../../service/ServiceServices';
import ServiceListing from './ServiceListing';
import { format } from 'date-fns';
import { formatZonedDate } from 'src/utils/formatZonedDate';

type Props = {};

const ServiceListingWrapper = (props: Props) => {
  const navigate = useNavigate();
  const [status] = useServiceStatusMutation();
  const [deleteService] = useDeleteServiceMutation();
  const { outlets } = useSelector((state: RootState) => state.auth);
  const { searchQuery, limit, page, appliedFilters } = useFilterPagination([
    'categoryId',
    'subCategoryId',
    'outletIds',
  ]);
  const { data, isLoading, totalData, totalPages } = useFetchData(
    useGetServicesQuery,
    {
      body: {
        limit,
        page,
        searchValue: searchQuery,
        searchIn: JSON.stringify(['serviceName']),
        filterBy: JSON.stringify(appliedFilters),
      },
    },
  );

  const handleDelete = (item: any, closeDialog: () => void) => {
    deleteService(item?._id).then((res: any) => {
      if (res?.error) {
        showToast('error', res?.error?.data?.message);
      } else {
        if (res?.data?.status) {
          showToast('success', res?.data?.message);
          closeDialog();
        } else {
          showToast('error', res?.data?.message);
        }
      }
    });
  };

  const { data: categoryData } = useFetchData(useGetCategoriesQuery, {
    body: { isPaginationRequired: false },
  });
  const { data: subCategoryData } = useFetchData(useGetSubCategoriesQuery, {
    body: { isPaginationRequired: false },
  });
  const filters: FilterType[] = [
    {
      filterType: 'multi-select',
      label: 'Category',
      fieldName: 'categoryId',
      options:
        categoryData?.map((el) => {
          return {
            label: el?.categoryName,
            value: el?._id,
          };
        }) || [],
      renderOption: (option) => option.label,
      isOptionEqualToSearchValue: (option, value) => {
        return option?.label.includes(value);
      },
    },
    {
      filterType: 'multi-select',
      label: 'Sub Category',
      fieldName: 'subCategoryId',
      options:
        subCategoryData?.map((el) => {
          return {
            label: el?.subCategoryName,
            value: el?._id,
          };
        }) || [],
      renderOption: (option) => option.label,
      isOptionEqualToSearchValue: (option, value) => {
        return option?.label.includes(value);
      },
    },
    {
      filterType: 'multi-select',
      label: 'Outlets',
      fieldName: 'outletIds',
      options:
        outlets?.map((el) => {
          return {
            label: el?.name,
            value: el?._id,
          };
        }) || [],
      renderOption: (option) => option.label,
      isOptionEqualToSearchValue: (option, value) => {
        return option?.label.includes(value);
      },
    },
  ];
  const handleStatusChanges = (
    item: any,
    closeDialog: () => void,
    setIsLoading: any,
  ) => {
    status(item?._id).then((res: any) => {
      if (res?.error) {
        showToast('error', res?.error?.data?.message);
      } else {
        if (res?.data?.status) {
          showToast('success', res?.data?.message);
          closeDialog();
        } else {
          showToast('error', res?.data?.message);
        }
      }
      setIsLoading(false);
    });
  };
const tableHeaders: TableHeader<Service>[] = [
  {
    fieldName: 'serviceName',
    headerName: 'Name',
    resizable: true,
    width: 220,
    stopPropagation: true,
  },
  {
    fieldName: 'serviceCode',
    headerName: 'Code',
    resizable: true,
    width: 130,
  },
  {
    fieldName: 'categoryName',
    headerName: 'Category',
    resizable: true,
    width: 200,
    align: 'start',
    renderCell: (item: any) =>
      item?.categoryName
        ?.map((c: any) => c.categoryNames)
        .join(', ') || '-',
  },
  {
    fieldName: 'subCategoryName',
    headerName: 'Sub Category',
    resizable: true,
    width: 200,
    renderCell: (item: any) =>
      item?.subCategoryName
        ?.map((c: any) => c.subCategoryNames)
        .join(', ') || '-',
  },
  {
    fieldName: 'sellingPrice',
    headerName: 'Price',
    resizable: true,
    width: 110,
    align: 'end',
    renderCell: (item) => (
      <div>
        {CURRENCY} {Number(item?.sellingPrice || 0).toFixed(2)}
      </div>
    ),
  },
  {
    fieldName: 'createdAt',
    headerName: 'Date',
    resizable: true,
    width: 130,
    stopPropagation: true,
    render: (row: any) => {
      const date = row.createdAt ? new Date(row.createdAt) : null;
      return date ? formatZonedDate(date) : '-';
    },
  },
  {
    fieldName: 'status',
    headerName: 'Active',
    resizable: false,           // 🚫 switch — fixed
    width: '100px',
    permissions: ['SERVICE_ACTIVE_DEACTIVE'],
    renderCell(item) {
      return (
        <div>
          <ATMSwitch
            checked={item?.isActive}
            onChange={(checked) => {
              ShowConfirmation({
                type: 'INFO',
                confirmationText: 'Yes',
                title: 'Are you sure ?',
                message: 'You really want to be change Status',
                onConfirm: (closeDialog, setIsLoading) =>
                  handleStatusChanges(item, closeDialog, setIsLoading),
              });
            }}
            activeLabel="Yes"
            deactiveLabel="No"
          />
        </div>
      );
    },
  },
];
  return (
    <>
      <ServiceListing
        tableHeaders={tableHeaders}
        rowData={data as Service[]}
        onAddNew={() => navigate('/service/add')}
        filterPaginationData={{
          totalCount: totalData,
          totalPages: totalPages,
        }}

        onEdit={(serviceId: string) => navigate(`/service/edit/${serviceId}`)}
        onDelete={handleDelete}
        isTableLoading={isLoading}
        filters={filters}
      />
    </>
  );
};

export default ServiceListingWrapper;
